<template>
  <page-wrapper>
    <template #title>
      <div class="h3">Новое Юридическое лицо</div>
      <v-spacer />
      <mobile-floating-bar>
        <div class="d-flex justify-end">
          <e-btn variant="secondary" @click="routerBack">Отмена</e-btn>
          <e-btn variant="primary" class="ml-4" :loading="isLegalLoading" @click="handleCreate"> Создать </e-btn>
        </div>
      </mobile-floating-bar>
    </template>

    <property-block>
      <template #text>
        <div class="paragraph-l font-600">Общая информация</div>
        <div class="paragraph-s_h text-secondary mt-2">
          Заполните поля, по&nbsp;которым мы&nbsp;сможем провалидировать юридическое лицо
        </div>
      </template>
      <template #content>
        <e-form ref="formRef" class="pt-2">
          <div class="d-grid row-gap-4">
            <legal-origin-select v-model="form.origin" />
            <e-radio-group
              v-if="isRuLegal"
              v-model="form.legalType"
              :items="legalTypeItems"
              inline
              class="py-2"
              @update:model-value="handleLegalTypeChange"
            />
            <e-combobox
              ref="innRef"
              :model-value="form.inn"
              :items="legalSuggestions"
              :rules="innRules"
              :maska="innMaska"
              :error-messages="form.legalError"
              item-title="data.inn"
              item-value="data.inn"
              label="ИНН"
              no-filter
              return-object
              :loading="isLegalLoading"
              @update:modelValue="handleLegalChange"
              @update:search="searchLegalSuggestions"
            >
              <template #item="{ item }">
                <div class="input-placeholder">{{ item.raw.data.opf.short }} {{ item.raw.data.name.full }}</div>
                <div class="paragraph-xs text-secondary">
                  {{ item.raw.data.kpp }} {{ item.raw.data.address?.value }}
                </div>
              </template>
            </e-combobox>
            <e-alert v-if="form.legalInBaseId" variant="info" thin closable>
              <div class="paragraph-s font-600">ИНН есть в базе действующих юридических лиц Алеан</div>
              <div class="paragraph-s text-secondary mt-1">
                Вы сможете изменить данные юридического лица через Поддержку после подтверждения администратором
                экстранета связи с вашим пространством
              </div>
            </e-alert>

            <multilanguage-field
              v-model="form.nameML"
              :rules="form.isLegalAvailable ? [required()] : []"
              :common-rules="[maxLength(255)]"
              label="Наименование"
              :disabled="!form.isLegalAvailable"
            />
            <e-input
              v-if="!isRuLegal || form.legalType === 1"
              v-model="form.kpp"
              :maska="kppMaska"
              :rules="kppRules"
              :label="isRuLegal ? 'КПП' : 'КПП (не обязательно)'"
              :disabled="!form.isLegalAvailable"
            />
            <e-input
              v-if="isRuLegal"
              v-model="form.ogrn"
              :rules="form.isLegalAvailable ? [length(form.legalType === 1 ? 13 : 15)] : []"
              :maska="form.legalType === 1 ? ogrnMask : ogrnipMask"
              label="ОГРН / ОГРНИП"
              :disabled="!form.isLegalAvailable"
            />
            <address-field
              v-model="form.addressDataML"
              :rules="form.isLegalAvailable ? [required()] : []"
              :common-rules="[maxLength(255)]"
              label="Юридический адрес"
              :disabled="!form.isLegalAvailable"
            />
            <e-checkbox
              v-if="isRuLegal"
              v-model="form.isNotTaxPayer"
              label="Не является плательщиком НДС"
              class="mt-2"
              :disabled="!form.isLegalAvailable"
            />
          </div>
        </e-form>
      </template>
    </property-block>

    <confirmation-dialog
      v-model="isConfirmationDialogOpen"
      :action="createAction"
      confirm-text="Создать"
      :loading="form.isLoading"
    >
      <div class="h3 mb-6">Создание юридического лица</div>
      <div class="text-secondary">
        После создания юридическое лицо будет отправлено на&nbsp;валидацию. Дальнейшее исправление данных юридического
        лица будет доступно только через Поддержку
      </div>
    </confirmation-dialog>
  </page-wrapper>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { LegalSuggestion as LegalSuggestionType } from '@/core/types/suggestions';
import type { MultilanguageValue } from '@/core/utils/languages';
import LegalOriginSelect from '@/core/components/common/LegalOriginSelect.vue';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import AddressField from '@/core/components/fields/AddressField.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EAlert, EBtn, ECheckbox, ECombobox, EForm, EInput, ERadioGroup, MultilanguageField } from '@/core/components/ui';
import { useSuggestions } from '@/core/composables/suggestions';
import { useRouterBack } from '@/core/composables/useRouterBack';
import { useSnackbar } from '@/core/composables/useSnackbar';
import useToggle from '@/core/composables/useToggle';
import {
  innOtherMask,
  innRuIPMask,
  innRuMask,
  kppOtherMask,
  kppRuMask,
  ogrnipMask,
  ogrnMask,
} from '@/core/consts/masks';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { TempItemStatus } from '@/core/utils/store/replicas';
import { isString } from '@/core/utils/type-guards';
import { length, maxLength, minLength, required } from '@/core/utils/validation';
import { type LegalDataInput, LegalOrigin } from '@/graphql/generated';
import { useLegalsStore } from '@/parts/legals/store/legals';
import { getDefaultMultilanguageValues } from '@/core/utils/languages';

const $router = useRouter();
const { routerBack } = useRouterBack();
const { showSnackbar } = useSnackbar();
const { getDadataAddressSuggestions, getLegalSuggestions } = useSuggestions();
const { getLegalIsFree, findFreeLegal, addLegal, addLegalExisting, legals } = useLegalsStore();
const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useToggle();

type AddressSuggestionType = { address: string; townName: string };

const form = reactive<{
  origin: LegalOrigin;
  legalType: 1 | 2;
  inn: string | null;
  nameML: MultilanguageValue[];
  kpp: string;
  ogrn: string;
  addressDataML: MultilanguageValue[];
  isNotTaxPayer: boolean;

  isLoading: boolean;
  isLegalAvailable: boolean;
  legalError: string;
  legalInBaseId: string | null;
}>({
  origin: LegalOrigin.Ru,
  legalType: 1,
  inn: null,
  nameML: getDefaultMultilanguageValues(),
  kpp: '',
  ogrn: '',
  addressDataML: getDefaultMultilanguageValues(),
  isNotTaxPayer: false,

  isLoading: false,
  isLegalAvailable: false,
  legalError: '',
  legalInBaseId: null,
});

const legalTypeItems = [
  { title: 'Юридическое лицо', value: 1 },
  { title: 'Индивидуальный предприниматель', value: 2 },
];

const formRef = ref();

const isRuLegal = computed(() => form.origin === LegalOrigin.Ru);

//#region Legal
const isLegalLoading = ref(false);
const legalSuggestions = ref<LegalSuggestionType[]>([]);

const innMaska = computed(() => {
  if (isRuLegal.value) {
    return form.legalType === 1 ? innRuMask : innRuIPMask;
  }

  return innOtherMask;
});

const innLength = computed(() => (form.legalType === 1 ? 10 : 12));
const innMinLength = 8;

const innRules = computed(() => {
  if (isRuLegal.value) {
    return [required(), length(innLength.value)];
  }

  return [required(), minLength(innMinLength)];
});

const searchLegalSuggestions = useDebounceFn(async (searchString: string) => {
  isLegalLoading.value = true;

  legalSuggestions.value = await getLegalSuggestions(searchString);

  isLegalLoading.value = false;
}, 300);

const handleLegalChange = async (value: LegalSuggestionType | string | null) => {
  form.legalError = '';
  form.legalInBaseId = null;

  if (!value) {
    form.inn = null;
  } else if (isString(value)) {
    form.inn = value;
  } else {
    form.inn = value.data.inn;
    form.kpp = value.data.kpp;
    form.ogrn = value.data.ogrn;
    
    const ruAddress = form.addressDataML.find(item => item.langCode === 'RU');
    if (ruAddress && value.data.address?.value) {
      ruAddress.value = value.data.address.value;
    }
    
    const ruName = form.nameML.find(item => item.langCode === 'RU');
    if (ruName) {
      ruName.value = value.data.name.full;
    }
  }

  await checkInn();
};

const handleLegalTypeChange = async () => {
  form.legalError = '';
  form.legalInBaseId = null;

  await checkInn();
};
//#endregion Legal

//#region КПП
const kppMaska = computed(() => (isRuLegal.value ? kppRuMask : kppOtherMask));

const kppRules = computed(() => {
  if (isRuLegal.value) {
    return form.isLegalAvailable ? [required(), length(9)] : [];
  }

  return [];
});
//#endregion КПП

//#region Address
const isSearchLoading = ref(false);
const addressSuggestions = ref<AddressSuggestionType[]>([]);

const searchAddresses = useDebounceFn(async (searchString: string) => {
  isSearchLoading.value = true;

  const result = await getDadataAddressSuggestions(searchString);

  isSearchLoading.value = false;

  addressSuggestions.value = result.map((item) => ({
    address: item.value,
    townName: item.data.city,
  }));
}, 500);
//#endregion Address

const innRef = ref();

const checkInn = useDebounceFn(async () => {
  if (!innRef.value.input.isValid) {
    return;
  }

  const inn = form.inn!;

  if (!inn) {
    return;
  }

  isLegalLoading.value = true;

  const legalIsFreeResult = await getLegalIsFree({ tin: inn });

  isLegalLoading.value = false;

  if (legalIsFreeResult?.errors) {
    return;
  }

  if (!legalIsFreeResult?.data?.isLegalFree) {
    form.legalError = 'Уже существует связь другого Пространства Экстранет с данным Юридическим лицом';
    form.isLegalAvailable = false;
    return;
  }

  isLegalLoading.value = true;

  const findFreeLegalResult = await findFreeLegal({ tin: inn });

  isLegalLoading.value = false;
  form.isLegalAvailable = false;

  if (findFreeLegalResult?.errors) {
    return;
  }

  if (findFreeLegalResult?.data?.findFreeLegal) {
    form.legalInBaseId = findFreeLegalResult.data.findFreeLegal.cid;
    form.kpp = findFreeLegalResult.data.findFreeLegal.data.details.kpp ?? '';
    form.ogrn = findFreeLegalResult.data.findFreeLegal.data.details.ogrn ?? '';
    
    if (findFreeLegalResult.data.findFreeLegal.data.nameML?.length) {
      form.nameML = findFreeLegalResult.data.findFreeLegal.data.nameML;
    } else {
      const ruName = form.nameML.find(item => item.langCode === 'RU');
      if (ruName && findFreeLegalResult.data.findFreeLegal.data.name) {
        ruName.value = findFreeLegalResult.data.findFreeLegal.data.name;
      } else if (ruName) {
        ruName.value = '';
      }
    }
    
    if (findFreeLegalResult.data.findFreeLegal.data.addressML?.length) {
      form.addressDataML = findFreeLegalResult.data.findFreeLegal.data.addressML;
    } else if (findFreeLegalResult.data.findFreeLegal.data.address) {
      const ruAddress = form.addressDataML.find(item => item.langCode === 'RU');
      if (ruAddress && findFreeLegalResult.data.findFreeLegal.data.address) {
        ruAddress.value = findFreeLegalResult.data.findFreeLegal.data.address;
      } else if (ruAddress) {
        ruAddress.value = '';
      }
    }

    return;
  }

  form.isLegalAvailable = true;
}, 500);

const filterFilledML = (mlValues: MultilanguageValue[]) => {
  return mlValues.filter(item => item.value && item.value.trim() !== '');
};

const handleCreate = async () => {
  const valid = await formRef.value?.validate();

  if (!valid || form.legalError) return;

  setIsConfirmationDialogOpen(true);
};

const createAction = async () => {
  const valid = await formRef.value?.validate();

  if (!valid || form.legalError) return;

  form.isLoading = true;

  const nameML = filterFilledML(form.nameML);
  const addressML = filterFilledML(form.addressDataML);

  const payload: LegalDataInput = {
    legalOrigin: form.origin,
    address: {
      addressML: addressML.length ? addressML : null,
      townName: addressML.find(item => item.langCode === 'RU')?.value?.split(',')[0] || '',
    },
    legalDetails: {
      tin: form.inn!,
    },
    nameML: nameML.length ? nameML : null,
    isNotTaxPayer: false,
  };

  if (isRuLegal.value) {
    payload.legalDetails.kpp = form.legalType === 1 ? form.kpp : undefined;
    payload.legalDetails.ogrn = form.ogrn;
    payload.isNotTaxPayer = form.isNotTaxPayer;
  } else {
    payload.legalDetails.kpp = form.kpp;
    payload.isNotTaxPayer = true;
  }

  const result = form.legalInBaseId
    ? await addLegalExisting({ legalCid: form.legalInBaseId })
    : await addLegal(payload);

  form.isLoading = false;

  if (result?.data) {
    const legal = 'addLegal' in result.data ? result.data.addLegal.legal : result.data.addLegalExisting.legal;

    legals.value.legals.tempItem = { ...legal!, tempItemStatus: TempItemStatus.Added };

    const description = nameML.find(item => item.langCode === 'RU')?.value || 
                        nameML.find(item => item.value)?.value || 
                        '';
    
    showSnackbar({ 
      type: 'success', 
      title: 'Новое юридическое лицо добавлено', 
      description 
    });

    await $router.push({ name: RouteNames.Legals });
  }
};
</script>

<style scoped lang="scss"></style>
